import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Zap, ChevronRight, ListChecks, Target, Coins } from "lucide-react";
import Btn from "../components/Btn";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import TryoutPacks from "../components/TryoutPacks";

const EXAM_COLOR = { CPNS: "var(--su-primary)", SNBT: "var(--su-success)" };

export default function Tryouts() {
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(null);
  const { user, setUser } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data.packages)).catch(() => {});
  }, []);

  const start = async (pkg) => {
    const needEnergy = pkg.energy_cost || 0;
    const needTokens = pkg.token_cost || 0;
    if (user.energy < needEnergy || user.tokens < needTokens) {
      toast.error("Token/energi tidak cukup. Mengarahkan ke halaman pembelian...");
      setTimeout(() => nav("/pricing"), 1200);
      return;
    }
    setBusy(pkg.package_id);
    try {
      const { data } = await api.post("/cbt/start", { package_id: pkg.package_id });
      setUser({ ...user, energy: user.energy - needEnergy, tokens: user.tokens - needTokens });
      nav(`/cbt/${data.attempt_id}`, { state: data });
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(apiErr(e) + " — mengarahkan ke halaman pembelian...");
        setTimeout(() => nav("/pricing"), 1200);
      } else {
        toast.error(apiErr(e));
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-black text-2xl sm:text-3xl">Pilih Tryout</h1>
        <p className="text-su-muted font-medium">Simulasi CBT mirip aslinya. Energi kamu: <span className="font-heading font-black text-su-energy">{user.energy}</span> ⚡</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {packages.map((p) => {
          const color = EXAM_COLOR[p.exam] || "var(--su-primary)";
          const total = p.sections.reduce((a, s) => a + s.count, 0);
          return (
            <div key={p.package_id} className="card-chunky p-6 flex flex-col" data-testid={`package-${p.package_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-heading font-black uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ color, background: `${color}22` }}>{p.exam}</span>
                  {p.mode === "drill" ? (
                    <span data-testid={`mode-badge-${p.package_id}`} className="flex items-center gap-1 text-[10px] font-heading font-black uppercase tracking-wider px-2.5 py-1 rounded-full text-su-energy" style={{ background: "var(--su-energy)22" }}>
                      <Target className="w-3 h-3" /> Drill Kilat
                    </span>
                  ) : (
                    <span data-testid={`mode-badge-${p.package_id}`} className="text-[10px] font-heading font-black uppercase tracking-wider px-2.5 py-1 rounded-full text-su-muted" style={{ background: "var(--su-track)" }}>
                      Tryout
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {p.token_cost > 0 && <span className="flex items-center gap-1 text-xs font-heading font-black text-su-secondaryShadow"><Coins className="w-4 h-4" /> {p.token_cost}</span>}
                  <span className="flex items-center gap-1 text-xs font-heading font-black text-su-energy"><Zap className="w-4 h-4 fill-current" /> {p.energy_cost}</span>
                </div>
              </div>
              <h3 className="font-heading font-black text-xl">{p.title}</h3>
              <p className="text-su-muted font-bold text-sm mb-4">{p.subtitle}</p>

              <div className="flex items-center gap-4 text-sm font-heading font-extrabold text-su-muted mb-4">
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {p.duration_minutes} mnt</span>
                <span className="flex items-center gap-1"><ListChecks className="w-4 h-4" /> {total} soal</span>
              </div>

              <div className="space-y-1.5 mb-5 flex-1">
                {p.sections.map((s) => (
                  <div key={s.category} className="flex items-center justify-between text-sm font-medium">
                    <span className="text-su-muted">{s.label}</span>
                    <span className="font-heading font-extrabold" style={{ color }}>{s.count}</span>
                  </div>
                ))}
              </div>

              <Btn data-testid={`start-${p.package_id}`} disabled={busy === p.package_id}
                onClick={() => start(p)} className="w-full"
                variant={p.exam === "SNBT" ? "success" : "primary"}>
                {busy === p.package_id ? "Menyiapkan..." : <>Mulai <ChevronRight className="w-5 h-5" /></>}
              </Btn>
            </div>
          );
        })}
      </div>

      <TryoutPacks />
    </div>
  );
}
